import { Button, IconButton } from "@hortis/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandLoading,
} from "@hortis/ui/command";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogTrigger,
} from "@hortis/ui/dialog";
import { useMediaQuery } from "@hortis/ui/hooks/media-query";
import { Check, Plus, XClose } from "@hortis/ui/icons";
import { Popover, PopoverContent, PopoverTrigger } from "@hortis/ui/popover";
import { Tag } from "@hortis/ui/tag";
import { useParams, useRouteContext } from "@tanstack/react-router";
import { useGetLocationsQuery } from "generated/graphql";
import { type ReactNode, useState } from "react";
import { useNavSites } from "src/components/nav-layout/site-context/use-nav-sites";
import { stopEventPropagation } from "src/features/collection/components/stop-event-propagation";
import type { LocationOption } from "src/features/create-accession/components/add-plant-material-form/schema";
import { twMerge } from "tailwind-merge";
import { NewLocationSheetContent } from "../location-drawer";

export const LocationPickerLabel = ({ value }: { value: LocationOption }) => (
  <div className="flex w-full flex-1 flex-wrap items-center gap-2">
    <span className="truncate text-grey-700">{value.name}</span>
    <span className="flex-1 truncate text-left font-normal text-grey-500">
      {value.description}
    </span>
  </div>
);

export const LocationPicker = ({
  value,
  onChange,
  "data-cy": dataCy,
  placeholder: _placeholder,
  children,
}: {
  value: LocationOption | undefined | null;
  onChange: (val: LocationOption | undefined | null) => void;
  "data-cy"?: string;
  placeholder?: string;
  children: ReactNode;
}) => {
  const isDesktop = useMediaQuery("md");
  const placeholder = _placeholder ?? "Select location...";
  const [open, setOpen] = useState(false);
  const [initialLocationName, setInitialLocationName] = useState<
    string | undefined
  >();
  const [search, setSearch] = useState("");
  const [drawerOpen, setDrawerOpen] = useState(false);

  const { subdomain } = useRouteContext({ from: "__root__" });
  const { siteSlug } = useParams({ strict: false });
  const settingsSites = useNavSites();
  const slug = siteSlug ?? settingsSites.activeSite?.urlSlug;
  const [{ data, fetching }, refetch] = useGetLocationsQuery({
    variables: {
      subdomain,
      siteSlug: slug ?? "",
    },
    pause: slug == null,
  });

  const handleChange = (location: LocationOption) => {
    if (value?.id === location.id) {
      onChange(null);
    } else {
      onChange(location);
    }
    setOpen(false);
  };

  const locations = data?.org?.site?.locationsConnection?.nodes;

  const commandContent = (
    <>
      <CommandEmpty>No results</CommandEmpty>
      {fetching && <CommandLoading />}
      <CommandList>
        <CommandGroup>
          {locations?.map((location) => (
            <CommandItem
              key={location.id}
              value={location.id}
              keywords={[location.name, location.description ?? ""]}
              onSelect={() => {
                handleChange({
                  id: location.id,
                  name: location.name,
                  description: location.description,
                });
                setSearch("");
              }}
              data-cy="location-picker-item"
              className="flex w-full justify-between"
            >
              <div className="flex w-full shrink gap-2 truncate">
                <span className="w-full max-w-fit truncate">
                  {location.name}
                </span>
                <span className="w-full max-w-fit truncate font-normal text-grey-500">
                  {location.description}
                </span>
              </div>
              {value?.id === location.id && (
                <Check className="text-primary-600" />
              )}
            </CommandItem>
          ))}
          {locations == null ||
          locations.length === 0 ||
          (search !== "" &&
            !locations.some(
              (l) => l.name.toLowerCase() === search.toLowerCase(),
            )) ? (
            <CommandItem
              value="new-location"
              onSelect={() => {
                setOpen(false);
                setDrawerOpen(true);
                setInitialLocationName(search);
                setSearch("");
              }}
              className="flex"
              data-cy="location-picker-new-location-item"
            >
              <Plus className="shrink-0 text-grey-500" />
              <div>
                New location
                {search !== "" && (
                  <>
                    :{" "}
                    <span className="flex-wrap break-all font-normal text-grey-500">
                      &apos;{search}&apos;
                    </span>
                  </>
                )}
              </div>
            </CommandItem>
          ) : null}
        </CommandGroup>
      </CommandList>
    </>
  );

  if (isDesktop) {
    return (
      <>
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger
            asChild
            data-cy={dataCy}
            onFocusCapture={stopEventPropagation}
          >
            {children}
          </PopoverTrigger>
          <PopoverContent className="z-99999 w-72 min-w-(--radix-popover-trigger-width)">
            <Command
              filter={(value, search, keywords) => {
                const extendValue = keywords?.join(" ") ?? "";
                if (
                  extendValue.toLowerCase().includes(search.toLowerCase()) ||
                  value === "new-location"
                ) {
                  return 1;
                }
                return 0;
              }}
            >
              <CommandInput
                placeholder={placeholder}
                value={search}
                onValueChange={setSearch}
                data-cy="location-picker-input"
              />

              {commandContent}
            </Command>
          </PopoverContent>
        </Popover>
        <Dialog open={drawerOpen} onOpenChange={setDrawerOpen}>
          <NewLocationSheetContent
            open={drawerOpen}
            onClose={() => {
              setDrawerOpen(false);
            }}
            initialValue={
              initialLocationName == null
                ? undefined
                : { name: initialLocationName }
            }
            onLocationCreation={(location) => {
              onChange(location);
              setDrawerOpen(false);
              refetch();
            }}
          />
        </Dialog>
      </>
    );
  }

  return (
    <>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger
          asChild
          data-cy={dataCy}
          onFocusCapture={stopEventPropagation}
        >
          {children}
        </DialogTrigger>
        <DialogContent className="max-w-none p-0!">
          <Command
            size="lg"
            filter={(value, search, keywords) => {
              const extendValue = keywords?.join(" ") ?? "";
              if (
                extendValue.toLowerCase().includes(search.toLowerCase()) ||
                value === "new-location"
              ) {
                return 1;
              }
              return 0;
            }}
          >
            <CommandInput
              placeholder={placeholder}
              value={search}
              onValueChange={setSearch}
              data-cy="location-picker-input"
            >
              <DialogClose asChild>
                <IconButton
                  icon={XClose}
                  size="xs"
                  ariaLabel="Close"
                  variant="tertiaryGray"
                />
              </DialogClose>
            </CommandInput>
            {commandContent}
          </Command>
        </DialogContent>
      </Dialog>
      <Dialog open={drawerOpen} onOpenChange={setDrawerOpen}>
        <NewLocationSheetContent
          open={drawerOpen}
          onClose={() => {
            setDrawerOpen(false);
          }}
          initialValue={
            initialLocationName == null
              ? undefined
              : { name: initialLocationName }
          }
          onLocationCreation={(location) => {
            onChange(location);
            setDrawerOpen(false);
            refetch();
          }}
        />
      </Dialog>
    </>
  );
};

export const LocationMultiPicker = ({
  value,
  onChange,
  "data-cy": dataCy,
}: {
  value: Array<LocationOption> | undefined | null;
  onChange: (val: Array<LocationOption> | undefined | null) => void;
  "data-cy"?: string;
}) => {
  const [open, setOpen] = useState(false);
  const { subdomain } = useRouteContext({ from: "/_layout/site/$siteSlug" });
  const { siteSlug } = useParams({ from: "/_layout/site/$siteSlug" });
  const [{ data, fetching }] = useGetLocationsQuery({
    variables: {
      subdomain,
      siteSlug,
    },
  });

  const handleChange = (location: LocationOption) => {
    if (value?.some((u) => u.id === location.id) === true) {
      onChange(value.filter((u) => u.id !== location.id));
    } else {
      onChange([...(value ?? []), location]);
    }
    setOpen(false);
  };

  const locations = data?.org?.site?.locationsConnection?.nodes;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger className="mx-auto w-fit" asChild>
        <Button
          className={twMerge(
            "min-h-[44px] w-full justify-between px-2.5! py-2! text-grey-500",
          )}
          testId={dataCy}
        >
          {value == null || value.length === 0 ? (
            <div className="flex flex-1 items-center gap-2 px-1">
              Pick a location
            </div>
          ) : (
            <div className="flex flex-wrap items-center gap-2">
              {value.map((location) => (
                <Tag key={location.id} spacing="icon">
                  {location.name}
                </Tag>
              ))}
            </div>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="z-99999 w-full min-w-(--radix-popover-trigger-width)">
        <Command
          filter={(_, search, keywords) => {
            const extendValue = keywords?.join(" ") ?? "";
            if (extendValue.toLowerCase().includes(search.toLowerCase())) {
              return 1;
            }
            return 0;
          }}
        >
          <CommandInput placeholder="Search..." />
          <CommandEmpty>No results</CommandEmpty>
          {fetching && <CommandLoading />}
          <CommandList>
            <CommandGroup>
              {locations?.map((location) => (
                <CommandItem
                  key={location.id}
                  value={location.id}
                  keywords={[location.name, location.description ?? ""]}
                  onSelect={() => {
                    handleChange({
                      id: location.id,
                      name: location.name,
                      description: location.description,
                    });
                  }}
                  data-cy="user-picker-item"
                >
                  <span className="truncate">{location.name}</span>
                  <span className="flex-1 truncate font-normal text-grey-500">
                    {location.description}
                  </span>
                  {value?.some((user) => user.id === location.id) === true && (
                    <Check className="text-primary-600" />
                  )}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};
